import type { ImportJob, ImportProgress, ImportSummary, ImportTemplate, ImportJobsResponse } from '@/types/import';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';

const getApiBaseUrl = (): string => {
  const baseUrl = getApiUrl() || getBackendUrl();
  return `${baseUrl}/api/Import`;
};

// Custom fetch function that ensures authorization header is added
const importFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const headers = new Headers(init?.headers);
  
  const authConfig = getAuthConfig();
  
  if (authConfig.getAccessToken) {
    try {
      const token = await authConfig.getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } catch (error) {
      console.error('Error getting access token:', error);
    }
  }

  // Always set X-Instance-Id header, with fallback if not configured
  const instanceId = authConfig.instanceId || 'default-instance';
  headers.set('X-Instance-Id', instanceId);

  return fetch(input, {
    ...init,
    headers,
  });
};

export const importService = {
  // Get CSV template structure
  async getTemplate(): Promise<ImportTemplate> {
    const response = await importFetch(`${getApiBaseUrl()}/template`);
    if (!response.ok) throw new Error('Failed to fetch template');
    return response.json();
  },

  // Upload CSV file and start import job
  async uploadFile(file: File): Promise<ImportJob> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await importFetch(`${getApiBaseUrl()}/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) throw new Error('Failed to upload file');
    return response.json();
  },

  // Get progress of a specific import job
  async getProgress(jobId: string): Promise<ImportProgress> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/progress`);
    if (!response.ok) throw new Error('Failed to fetch progress');
    return response.json();
  },

  // Get details of a specific import job
  async getJob(jobId: string): Promise<ImportJob> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}`);
    if (!response.ok) throw new Error('Failed to fetch job details');
    return response.json();
  },

  // Get all import jobs
  async getJobs(): Promise<ImportJobsResponse> {
    const response = await importFetch(`${getApiBaseUrl()}/jobs`);
    if (!response.ok) throw new Error('Failed to fetch jobs');
    return response.json();
  },

  // Cancel an import job
  async cancelJob(jobId: string): Promise<void> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to cancel job');
  },

  // Get import summary
  async getSummary(jobId: string): Promise<ImportSummary> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/summary`);
    if (!response.ok) throw new Error('Failed to fetch summary');
    return response.json();
  },

  // Download error report
  async downloadErrorReport(jobId: string): Promise<Blob> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/error-report`);
    if (!response.ok) throw new Error('Failed to download error report');
    return response.blob();
  },

  // Delete a specific import job
  async deleteJob(jobId: string): Promise<void> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete job');
    }
  },

  async clearHistory(): Promise<{ deletedCount: number }> {
    const response = await importFetch(`${getApiBaseUrl()}/history/clear`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to clear history');
    return response.json();
  },

  // Generate and download CSV template
  generateTemplateCSV(): void {
    const headers = [
      'Asset Code',
      'Asset Name',
      'Category',
      'Sub Category',
      'Location',
      'Department',
      'Status',
      'Acquisition Cost',
      'Acquisition Date',
      'Currency',
      'Depreciation Method',
      'Useful Life (Years)',
      'Residual Value',
      'Serial Number',
      'Manufacturer',
      'Model',
      'Barcode',
      'Assigned To',
      'Supplier Name',
      'Purchase Order Number',
      'Insurance Policy Number',
      'Insurance Provider',
      'Insurance Expiry Date',
      'Insurance Value',
      'Warranty Provider',
      'Warranty Expiry Date',
      'Description',
      'Notes',
    ];

    const sampleRow = [
      'AST-2024-001',
      'Laptop Dell XPS 15',
      'IT Equipment',
      'Computers',
      'Head Office',
      'IT Department',
      'Active',
      '1500.00',
      '2024-01-15',
      'USD',
      'Straight Line',
      '5',
      '100.00',
      'SN123456789',
      'Dell',
      'XPS 15 9520',
      'BC123456',
      'John Doe',
      'Tech Supplier Inc',
      'PO-2024-001',
      'POL-2024-001',
      'Insurance Co',
      '2025-01-15',
      '1500.00',
      'Dell Inc',
      '2027-01-15',
      'High-performance laptop for development',
      'Purchased for new employee',
    ];

    const csvContent = [
      headers.join(','),
      sampleRow.join(','),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'asset_import_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
};
